/** biome-ignore-all lint/correctness/useUniqueElementIds: <explanation> */
/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: <explanation> */
import type { MetaData } from '@bulknutrients/bulk-shared-lib/interfaces/MetaData';
import type {
  BaseProduct,
  Product,
  ProductDescription,
  S3LabResult,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type { Review } from '@bulknutrients/bulk-shared-lib/interfaces/Review';
import type {
  FAQ,
  SanityImage,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { isVariantAvailable } from '@bulknutrients/bulk-shared-lib/lib/product';
import { getProductAlertById } from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/alerts';
import { CLOUDINARY_ASSET_FIELDS } from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/snippets';
import {
  cdnClient,
  cdnClient as client,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import {
  getFPTProducts,
  getLabResultsByProduct,
  getSingleProductById,
  getSingleProductByPath,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/productData';
import {
  getBreadcrumbs,
  getMetaData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/products/metadata';
import { getAllProductSlugs } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/slugs';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { Aside } from '@components/Aside/Aside';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button } from '@components/Button';
import { FPTProductsCarousel } from '@components/Carousels/FPTProductsCarousel';
import { Container } from '@components/Container';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { ProductLayout } from '@components/Layouts';
import { CustomImage } from '@components/Media/Image/Image';
import { QuickAddModal } from '@components/Modal/QuickAddModal';
import type { ContentTypes } from '@components/PortableText/PortableText';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { AverageRating } from '@components/Product/AverageRating';
import { ImageGallerySection } from '@components/Product/ImageGallery';
import { ProductInformation } from '@components/Product/PageSections/ProductInformation';
import { ProductAccordion } from '@components/Product/ProductAccordion';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useLocalStorage } from '@hooks/useLocalStorage';
// import { useLocalStorage } from '@hooks/useLocalStorage';
import { useStockLevel } from '@hooks/useStockLevel';
import { useWindowSize } from '@hooks/useWindowSize';
import { log } from '@lib/logs';
import {
  convertFAQToStructedData,
  createProductStructuredData,
  getStructuredDataImageUrl,
} from '@lib/structured';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { getReviewsHandler } from 'pages/api/reviews';
import {
  getProductInStockSS,
  stockLevelHandlerFetch,
} from 'pages/api/stock-level';
import {
  forwardRef,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useSWR from 'swr';

const ProductTitle = forwardRef<HTMLDivElement, { product: Product }>(
  ({ product }, ref) => (
    <div>
      <h2 className="flex items-center pt-5 text-2xl font-bold italic leading-none tracking-tighter text-black dark:text-white sm:text-left">
        {product.name}
      </h2>
      {product.tgaStatement ? (
        <p className="mt-0">{product.tgaStatement}</p>
      ) : null}

      <AverageRating
        averageRating={product.averageRating}
        reviews={product.totalReviews}
        ref={ref}
      />
    </div>
  )
);

ProductTitle.displayName = 'ProductTitle';

function AlertContent({ alert }) {
  const isSingleCloudinaryAsset =
    alert.content.length === 1 && alert.content[0]._type === 'cloudinary.asset';

  if (isSingleCloudinaryAsset) {
    return (
      <PortableTextBlockRenderer content={alert.content} customClass="mb-2" />
    );
  }

  return (
    <Aside>
      <PortableTextBlockRenderer content={alert.content} />
    </Aside>
  );
}

const fetcher = async ([url, productId]) => {
  const params = {
    id: productId,
  };

  const result = await client.fetch(getProductAlertById, params);

  if (result) {
    return result;
  }

  const error = new Error('Unable to fetch product alert');
  throw error;
};

export interface Props {
  product: Product;
  breadcrumbs: { title: string; path: string }[];
  meta: MetaData;
  labResults: S3LabResult[];
  fptProducts: BaseProduct[];
  faqStructuredData: ReturnType<typeof convertFAQToStructedData>;
  isPreview: boolean;
  serverSideStockLevels?: Record<number, boolean>;
  headerDescription: ContentTypes[];
  bodyDescription: ProductDescription[];
  reviews: Review[];
}

const IMAGE_LIMIT = 8;

export default function ProductPage({
  product,
  breadcrumbs, // used in layout
  meta,
  labResults,
  fptProducts,
  faqStructuredData,
  isPreview, // used in layout
  serverSideStockLevels,
  headerDescription,
  bodyDescription,
  reviews,
}: Props): ReactElement {
  // Hooks
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const addToCartRef = useRef(null);

  const { headerScrollClass } = useContext<UIContextInterface>(UIContext);
  const { inStock, stockLevels } = useStockLevel(product.entityId.toString(), {
    fallbackData: serverSideStockLevels,
  });
  const { customer } = useCustomer();
  const { width } = useWindowSize();
  const isXlDesktop = width && width >= 1280;
  const { setRecentlyViewedProduct } = useLocalStorage();
  const router = useRouter();

  const { data: alert, error } = useSWR(
    ['/api/productAlerts', product.entityId],
    fetcher
  );

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
  });

  const [variantImages, setVariantImages] = useState(
    product.variants.reduce((acc, cur) => {
      const available = isVariantAvailable(
        cur,
        customer ? customer.customerGroupId : undefined
      );
      if (available && cur.images.length > 0) {
        acc.push(...cur.images);
      }
      return acc;
    }, [] as SanityImage[])
  );
  const [offScreen, setOffScreen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [images, setImages] = useState([...product.images, ...variantImages]);

  useEffect(() => {
    setRecentlyViewedProduct({
      product: product,
      viewedTime: Date.now(),
    });
  }, [product, setRecentlyViewedProduct]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await cdnClient.fetch(
        `*[_id == $id][0]{
          ...products[product._ref == $productId][0]{
            "productPageImage": coalesce(productPageImage{${CLOUDINARY_ASSET_FIELDS}}, productCardImage{${CLOUDINARY_ASSET_FIELDS}}),
            "selectedVariant": variant->variantId,
          }
        }`,
        { id: router.query.campaign, productId: product._id }
      );
      if (data) {
        // get the options to select the correct variant on the product page
        const variant = product.variants.find(
          (v) => v.variantId === data.selectedVariant
        );

        if (variant) {
          variant.options.forEach((o) => {
            rhfMethods.setValue(o.optionId.toString(), o.valueId.toString(), {
              shouldDirty: true,
            });
          });
        }
      }

      setImages((prev) => {
        // add the campaign product page image to the top of the gallery images
        const newImage = data?.productPageImage;
        if (newImage) {
          return [newImage, ...prev].slice(0, 4);
        }
        return prev;
      });
    };
    if (router.query.campaign) {
      // If there is a campaign query param, pull sku and image from campaign
      fetchData();
    }
  }, [router.query.campaign, product._id, product.variants, rhfMethods]);

  // Handle events for sticky add to cart
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === addToCartRef.current) {
            // Use a simple check: if the element is not intersecting AND
            // its top is above the viewport (negative), it's scrolled past
            const hasScrolledPast =
              !entry.isIntersecting && entry.boundingClientRect.top < 0;

            // Only update state if it's actually changing to prevent re-renders
            setOffScreen((prev) => {
              if (prev !== hasScrolledPast) {
                return hasScrolledPast;
              }
              return prev;
            });
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '-123px 0px 0px 0px', // Account for header height
        threshold: [0, 0.1], // Trigger when any part enters/exits
      }
    );

    if (addToCartRef.current) observer.observe(addToCartRef.current);

    return () => observer.disconnect();
  }, []); // Empty dependency array - only run once on mount

  // Pull images out of variants
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setVariantImages(
        product.variants.reduce((acc, cur) => {
          const available = isVariantAvailable(
            cur,
            customer ? customer.customerGroupId : undefined
          );
          if (available && cur.images.length > 0) {
            acc.push(...cur.images);
          }
          return acc;
        }, [] as SanityImage[])
      );
    }
    return function cleanup() {
      mounted = false;
    };
  }, [customer, product.variants, product]);

  // When product changes, reset things
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setImages([...product.images, ...variantImages]);
    }

    return function cleanup() {
      mounted = false;
    };
  }, [product, variantImages]);

  useEffect(() => {
    // if page is directed to with hash to scroll to:
    // triple check if accordion tab is closed (should be) and click
    // scroll to section
    // delay is required to ensure accordion is mounted and if rogue hash is used, nothing happens

    // Try to open the section after a short delay to allow mount
    setTimeout(() => {
      if (window.location.hash && accordionRef.current) {
        const hash = window.location.hash.slice(1);

        const triggerElement = accordionRef.current?.querySelector(
          `[id="${hash}"]`
        );
        if (triggerElement && triggerElement instanceof HTMLElement) {
          if (triggerElement.parentElement?.hasAttribute('open')) {
            triggerElement.scrollIntoView({ behavior: 'smooth' });
          } else {
            triggerElement.click();
            // seems fine to scroll directly after, consider adding another delay if necessary
            triggerElement.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    }, 200);
  }, [product.entityId, accordionRef]);

  const RendererOverride = {
    block: {
      h2: ({ children }: { children?: ReactNode }) => (
        <h2 className="mt-10 pb-2 font-bold italic text-orange [&+.hr]:hidden has-[+.hr]:mb-5">
          {children}
        </h2>
      ),
      h3: ({ children }: { children?: ReactNode }) => (
        <>
          <HorizontalDivider className="via-grey-mid" />
          <h3 className="font-bold">{children}</h3>
        </>
      ),
    },
  };

  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        {faqStructuredData ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqStructuredData),
            }}
          />
        ) : null}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              createProductStructuredData(
                product,
                process.env.NEXT_PUBLIC_SERVER_URL || ''
              )
            ),
          }}
        />
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${product.slug}`
          }
        />
      </Head>
      <Script
        id="klaviyo-tracker"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
              var _learnq = _learnq || [];
              var item = {
                "ProductName": "${product.name}",
                "ProductID": "${product.entityId}",
                "SKU": "${product.sku}",
                "ImageURL": "${getStructuredDataImageUrl(
                  product.images.filter((i) => i.defaultImage)[0] ??
                    product.images[0]
                )}",
                "URL": "https://bulknutrients.com.au${product.slug}",
                "Price": "${product.pricing.price.value}",
              };
              _learnq.push(["track", "Viewed Product", item]);

              _learnq.push(["trackViewedItem", {
                "Title": item.ProductName,
                "ItemId": item.ProductID,
                "ImageUrl": item.ImageURL,
                "Url": item.URL,
                "Metadata": {
                  "Price": item.Price,
                }
              }]);
              `,
        }}
      />
      <Container
        as="main"
        id="main-content"
        className="bg-white dark:bg-black grid-cols-[1fr_min(var(--width-max-width-menu),calc(100%-16px))_1fr] xl:grid-cols-[max(150px,calc((100vw-1500px)/2))_min(1500px,calc(100vw-300px))_max(150px,calc((100vw-1500px)/2))]"
      >
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,calc(100%-400px))_400px] gap-4">
          {/* Section for images, body copy, ctas etc */}
          <section id="left-content" className="order-2 xl:order-1">
            <div className="hidden xl:block">
              <ImageGallerySection images={images.slice(0, IMAGE_LIMIT)} />
            </div>
            <div className="w-full overflow-hidden mt-5">
              {alert && (
                <div>
                  {!error && alert && Object.keys(alert).length > 0 && (
                    <AlertContent alert={alert} />
                  )}
                </div>
              )}
              {product.cta.length > 0 && (
                <div className="hidden md:flex flex-col flex-nowrap items-center gap-6 peer-default:pt-0 xl:flex-row xl:flex-wrap xl:items-start">
                  {product.cta.map((cta) => (
                    <div className="mx-auto md:mt-5" key={cta._key}>
                      <CustomImage image={cta} width={2200} />
                    </div>
                  ))}
                </div>
              )}
              {product.mobileCta.length > 0 && (
                <div className="flex flex-col flex-nowrap items-center gap-6 peer-default:pt-0 md:hidden">
                  {product.mobileCta.map((cta) => (
                    <div className="mx-auto md:mt-5" key={cta._key}>
                      <CustomImage image={cta} width={2200} />
                    </div>
                  ))}
                </div>
              )}
              <div
                ref={accordionRef}
                className="w-full justify-self-center mt-5"
              >
                <PortableTextBlockRenderer
                  content={headerDescription}
                  componentsOverride={RendererOverride}
                />
                <ProductAccordion
                  ref={accordionRef}
                  description={bodyDescription}
                  product={product}
                  labResults={labResults}
                  reviews={reviews}
                />
              </div>
              <FPTProductsCarousel relatedProducts={fptProducts} />
            </div>
          </section>

          {/* Section for main body */}
          <section
            id="product-head-details"
            className="order-1 xl:order-2"
            data-testid="product-head"
            data-product-id={product.entityId}
          >
            <Breadcrumbs crumbs={breadcrumbs} />
            <ProductTitle product={product} ref={accordionRef} />
            <div className="xl:hidden">
              <ImageGallerySection images={images.slice(0, IMAGE_LIMIT)} />
            </div>
            <div ref={addToCartRef}>
              <FormProvider {...rhfMethods}>
                <ProductInformation
                  product={product}
                  inStock={inStock}
                  stockLevels={stockLevels ?? serverSideStockLevels}
                  rhfMethods={rhfMethods}
                  offScreen={offScreen && isXlDesktop && inStock}
                  compactOverride={offScreen && isXlDesktop && inStock}
                />
              </FormProvider>
            </div>
          </section>
        </div>
      </Container>

      {inStock && (
        <div
          className={`fixed flex justify-self-center z-1 drop-shadow-lg ${headerScrollClass === 'hide' ? 'top-0' : 'top-[123px]'} ${offScreen ? 'block' : 'hidden'} xl:hidden`}
        >
          <Button
            className="w-screen rounded-none"
            onClick={() => setShowQuickAdd(true)}
          >
            Add to cart
          </Button>
        </div>
      )}
      {showQuickAdd && (
        <QuickAddModal
          show={showQuickAdd}
          setShow={setShowQuickAdd}
          product={product}
          isMobile={false}
          compactOverride={true}
        />
      )}
    </>
  );
}

ProductPage.getLayout = function getLayout(
  page: ReactElement & { props: Props }
) {
  return <ProductLayout {...page.props}>{page}</ProductLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllProductSlugs();
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    const product = preview
      ? await getSingleProductById((previewData as { id: string }).id)
      : await getSingleProductByPath({
          slug: (params?.product?.toString() as string).split(',').join('/'),
        });

    if (!product) {
      // try redirect
      const redirect = await getRedirect(
        `/products/${params?.product?.toString()}` as string
      );
      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // no product and no redirect, serve 404
      return {
        notFound: true,
      };
    }

    // Keep product pages renderable even if non-critical enrichments fail.
    const [
      serverSideStock,
      serverSideStockLevels,
      breadcrumbs,
      meta,
      labResults,
      fptProducts,
      reviews,
    ] = await Promise.all([
      getProductInStockSS(product.entityId).catch((error) => {
        log({
          error,
          location: `getStaticProps stock /products/${params?.product?.toString()}`,
        });
        return true;
      }),
      stockLevelHandlerFetch(product.entityId.toString()).catch((error) => {
        log({
          error,
          location: `getStaticProps stockLevels /products/${params?.product?.toString()}`,
        });
        return {} as Record<number, boolean>;
      }),
      getBreadcrumbs(product.entityId).catch((error) => {
        log({
          error,
          location: `getStaticProps breadcrumbs /products/${params?.product?.toString()}`,
        });
        return [{ title: 'home', path: '/' }];
      }),
      getMetaData(product.slug).catch((error) => {
        log({
          error,
          location: `getStaticProps metadata /products/${params?.product?.toString()}`,
        });
        return {
          metaTitle: product.name,
          metaDescription: product.shortDescription ?? '',
          noIndex: false,
        } as MetaData;
      }),
      getLabResultsByProduct(product).catch((error) => {
        log({
          error,
          location: `getStaticProps labResults /products/${params?.product?.toString()}`,
        });
        return [] as S3LabResult[];
      }),
      getFPTProducts(product.entityId).catch((error) => {
        log({
          error,
          location: `getStaticProps fptProducts /products/${params?.product?.toString()}`,
        });
        return [] as BaseProduct[];
      }),
      getReviewsHandler({
        pageSize: 10,
        page: 0,
        productId: product.entityId,
      }).catch((error) => {
        log({
          error,
          location: `getStaticProps reviews /products/${params?.product?.toString()}`,
        });
        return [] as Review[];
      }),
    ]);

    product.serverSideStock = serverSideStock;

    let faqs: { question: string; answer: string }[];
    if (product.description) {
      faqs = (
        product.description
          .flatMap((d) => d.contentBody)
          .filter((c) => c && c._type === 'faq') as FAQ[]
      ).map((faq) => ({
        question: faq.question,
        answer: faq.answer
          .map((a) => a.children.map((c) => c.text).join(' '))
          .join(' '),
      }));
    } else faqs = [];

    const faqStructuredData = convertFAQToStructedData(faqs);

    // If there is a product carousel, add the BC data that is needed for a BaseProduct
    // Get all products out
    const products = [] as Array<any>;
    for (const description of product.description) {
      if (description.contentBody) {
        for (const content of description.contentBody) {
          if (
            content._type === 'carousel' &&
            Array.isArray(content.items) &&
            content.items[0]?._type === 'product'
          ) {
            for (const item of content.items) {
              products.push(item);
            }
          }
        }
      }
    }

    // Map products
    const mappedProducts = await addBigCommerceDataToBaseProducts(products);

    // Replace them
    product.description = product.description.map((description) => {
      if (description.contentBody) {
        description.contentBody = description.contentBody.map((content) => {
          if (
            content._type === 'carousel' &&
            Array.isArray(content.items) &&
            content.items[0]?._type === 'product'
          ) {
            const mappedItems = content.items.map((item) =>
              mappedProducts.find((p) => p.entityId === item.entityId)
            );
            return {
              ...content,
              items: mappedItems,
            };
          }
          return content;
        });
      }
      return description;
    });

    // Separate into accordion and main description blocks
    // find where the header description ends, should be the first instance of an H2 | H3
    // if no instance of H2 | H3, then show the whole first content body and remove the product description tab from the accordion
    const firstDescriptionBlock = product.description?.[0]?.contentBody ?? [];
    const hIndex = firstDescriptionBlock.findIndex(
      (d) => d.style === 'h3' || d.style === 'h2'
    );
    const hasHeading = hIndex !== -1;

    const headerDescription = firstDescriptionBlock.slice(
      0,
      hasHeading ? hIndex : undefined
    );

    const bodyDescription = hasHeading
      ? [
          {
            ...product.description[0],
            contentBody: firstDescriptionBlock.slice(hIndex),
          },
          ...product.description.slice(1),
        ]
      : product.description.slice(1);

    return {
      props: {
        product,
        breadcrumbs,
        meta,
        labResults,
        fptProducts,
        faqStructuredData,
        isPreview: preview ?? false,
        serverSideStockLevels,
        headerDescription,
        bodyDescription,
        reviews,
      },
    };
  } catch (error) {
    log({
      error,
      location: `getStaticProps /products/${params?.product?.toString()}`,
    });
    throw error;
  }
};
